import { useState, useCallback, useEffect, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';

const PLAYBACK_ERROR = "Não deu para ouvir agora.";

export const useTextToSpeech = () => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentText, setCurrentText] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const playingRef = useRef(false);
  const generationRef = useRef(0);

  const stop = useCallback(() => {
    generationRef.current += 1;
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    playingRef.current = false;
    setIsPlaying(false);
    setCurrentText(null);
  }, []);

  useEffect(() => {
    return () => {
      stop();
    };
  }, [stop]);

  const speak = useCallback(async (text: string) => {
    if (playingRef.current) {
      stop();
      setError(null);
      return;
    }

    const token = generationRef.current;
    try {
      setError(null);
      playingRef.current = true;
      setIsPlaying(true);
      setCurrentText(text);

      const { data, error: invokeError } = await supabase.functions.invoke('text-to-speech', {
        body: { text },
      });

      if (token !== generationRef.current) return;

      if (invokeError) {
        console.error('Error calling text-to-speech:', invokeError);
        stop();
        setError(PLAYBACK_ERROR);
        return;
      }

      if (!data?.audioContent) {
        stop();
        setError(PLAYBACK_ERROR);
        return;
      }

      // Convert base64 to audio
      const audioBlob = new Blob(
        [Uint8Array.from(atob(data.audioContent), (c) => c.charCodeAt(0))],
        { type: 'audio/mpeg' }
      );
      const audioUrl = URL.createObjectURL(audioBlob);

      if (token !== generationRef.current) {
        URL.revokeObjectURL(audioUrl);
        return;
      }

      // Create and play audio
      const audio = new Audio(audioUrl);
      audioRef.current = audio;

      audio.onended = () => {
        if (token !== generationRef.current) return;
        playingRef.current = false;
        setIsPlaying(false);
        setCurrentText(null);
        URL.revokeObjectURL(audioUrl);
      };

      audio.onerror = () => {
        URL.revokeObjectURL(audioUrl);
        if (token !== generationRef.current) return;
        stop();
        setError(PLAYBACK_ERROR);
      };

      await audio.play();
    } catch (error) {
      console.error('Error in speak:', error);
      if (token !== generationRef.current) return;
      stop();
      setError(PLAYBACK_ERROR);
    }
  }, [stop]);

  return {
    speak,
    stop,
    isPlaying,
    currentText,
    error,
  };
};
